import React, { useState, useEffect, useCallback } from 'react';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { apiService } from '../services/api';
import { CostLibraryItem, CostLibraryFilter, Category, PagedResult } from '../types/api';
import DarkLayout from './DarkLayout';
import { HeaderPrimaryAddButton } from './HeaderPrimaryAddButton';
import SearchBar from './SearchBar';
import Pagination from './Pagination';
import CostItemDialog from './CostItemDialog';
import { formatCurrency } from '../utils/formatCurrency';
import { useAuth } from '../contexts/AuthContext';
import { hasPermission, permissions } from '../constants/permissions';
import {
  ListPageRoot,
  ListPageToolbar,
  ListPageFilterRow,
  listPageFilterPillActive,
  listPageFilterPillInactive,
  ListPageTableSurface,
  ListPageContentSection,
  ListPageMobileStack,
  ListPageInlineError,
  ListPageTableHeadRow,
  ListPageTh,
  listPageTableBodyClass,
  listPageTableRowClass,
  ListPageTablePlaceholder,
  ListPageMobilePlaceholder,
} from './listPage';

const CostLibrary: React.FC = () => {
  const { user } = useAuth();
  const canCreateCostItem = hasPermission(user, permissions.costLibrary.create);
  const canEditCostItem = hasPermission(user, permissions.costLibrary.edit);
  useDocumentTitle('Cost Library');
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pagedResult, setPagedResult] = useState<PagedResult<CostLibraryItem> | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<number | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [sortBy, setSortBy] = useState<string>('itemName');
  const [sortDescending, setSortDescending] = useState<boolean>(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedCostItemId, setSelectedCostItemId] = useState<number | undefined>(undefined);
  const [dialogError, setDialogError] = useState<string>('');

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const currentFilter: CostLibraryFilter = {
        categoryId: selectedCategory || undefined,
        searchTerm: searchTerm || undefined,
        pageNumber: currentPage,
        pageSize: itemsPerPage,
        sortBy: sortBy,
        sortDescending: sortDescending,
      };
      const result = await apiService.getCostLibraryItems(currentFilter);
      setPagedResult(result);
    } catch (err) {
      console.error('Error loading cost library items:', err);
      setError('Failed to load cost library items');
    } finally {
      setLoading(false);
    }
  }, [selectedCategory, searchTerm, currentPage, itemsPerPage, sortBy, sortDescending]);

  const loadCategories = useCallback(async () => {
    try {
      const cats = await apiService.getCategories();
      setCategories(cats);
    } catch (err) {
      console.error('Error loading categories:', err);
    }
  }, []);

  useEffect(() => {
    loadCategories();
  }, [loadCategories]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedCategory]);

  const handleSort = (column: string) => {
    if (sortBy === column) {
      setSortDescending(!sortDescending);
    } else {
      setSortBy(column);
      setSortDescending(false);
    }
    setCurrentPage(1);
  };

  const formatOutput = (item: CostLibraryItem) => {
    if (item.outputRate && item.outputRateUnitSymbol) {
      return `${item.outputRate} ${item.outputRateUnitSymbol}`;
    }
    return 'N/A';
  };

  const getCategoryBadgeColor = (categoryName: string) => {
    const lowerName = categoryName.toLowerCase();
    if (lowerName.includes('materials')) {
      return 'bg-green-900 text-green-300';
    } else if (lowerName.includes('labor')) {
      return 'bg-yellow-900 text-yellow-300';
    } else if (lowerName.includes('equipment')) {
      return 'bg-indigo-900 text-indigo-300';
    }
    return 'bg-primary/20 text-primary';
  };

  const getSortIcon = (column: string) => {
    if (sortBy?.toLowerCase() !== column.toLowerCase()) {
      return 'unfold_more';
    }
    return sortDescending ? 'arrow_downward' : 'arrow_upward';
  };

  const items = pagedResult?.items || [];
  const totalPages = pagedResult?.totalPages || 0;

  const handleOpenAddDialog = () => {
    setSelectedCostItemId(undefined);
    setDialogOpen(true);
  };

  const handleOpenEditDialog = (costItemId: number) => {
    setSelectedCostItemId(costItemId);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedCostItemId(undefined);
    setDialogError('');
  };

  const handleDialogSuccess = () => {
    fetchData();
    handleCloseDialog();
  };

  const handleDialogError = (message: string) => {
    setDialogError(message);
  };

  const thSort = (label: string, column: string) => (
    <th
      className="px-5 py-4 text-[10px] font-bold uppercase tracking-widest text-slate-500 cursor-pointer text-left"
      scope="col"
      onClick={() => handleSort(column)}
    >
      <div className="flex items-center gap-1">
        {label}
        <span className="material-symbols-outlined text-base">{getSortIcon(column)}</span>
      </div>
    </th>
  );

  return (
    <DarkLayout
      title="Construction Cost Library"
      activeTab="costlibrary"
      headerRight={
        canCreateCostItem ? (
          <HeaderPrimaryAddButton
            label="Add New Item"
            onClick={handleOpenAddDialog}
            mobileAriaLabel="Add new item"
          />
        ) : null
      }
    >
      <ListPageRoot>
        <p className="px-4 lg:px-5 pt-2 text-sm text-slate-400">
          Search and filter unique item costs from various suppliers and regions.
        </p>

        <ListPageToolbar className="items-stretch md:items-center">
          <SearchBar
            variant="listPage"
            placeholder="Search by item name or SKU"
            value={searchTerm}
            onChange={setSearchTerm}
            className="w-full md:max-w-md"
          />
          <div className="flex flex-col gap-3 w-full md:flex-1 md:min-w-0">
            <ListPageFilterRow className="flex-wrap w-full">
              {categories.map((cat) => (
                <button
                  key={cat.categoryId}
                  type="button"
                  onClick={() => {
                    const newCategory = selectedCategory === cat.categoryId ? null : cat.categoryId;
                    setSelectedCategory(newCategory);
                  }}
                  className={`flex cursor-pointer items-center justify-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${
                    selectedCategory === cat.categoryId
                      ? listPageFilterPillActive
                      : listPageFilterPillInactive
                  }`}
                >
                  {selectedCategory === cat.categoryId && (
                    <span className="material-symbols-outlined text-base">check_circle</span>
                  )}
                  <span>{cat.categoryName}</span>
                </button>
              ))}
              <button
                type="button"
                className="text-sm font-medium text-primary hover:underline ml-auto shrink-0"
                onClick={() => setSelectedCategory(null)}
              >
                Clear All
              </button>
            </ListPageFilterRow>
          </div>
        </ListPageToolbar>

        {error && <ListPageInlineError>{error}</ListPageInlineError>}

        <ListPageContentSection>
          <ListPageTableSurface>
            <div className="overflow-x-auto">
              <table className="w-full text-sm text-left border-collapse">
                  <thead>
                    <ListPageTableHeadRow>
                      {thSort('Item Name', 'itemName')}
                      {thSort('Category', 'category')}
                      {thSort('Unit (Type)', 'unit')}
                      {thSort('Lowest Price', 'price')}
                      <ListPageTh>Output</ListPageTh>
                      <ListPageTh className="text-right">Actions</ListPageTh>
                    </ListPageTableHeadRow>
                  </thead>
                  <tbody className={listPageTableBodyClass}>
                    {loading ? (
                      <tr>
                        <td colSpan={6}>
                          <ListPageTablePlaceholder>Loading items...</ListPageTablePlaceholder>
                        </td>
                      </tr>
                    ) : items.length === 0 ? (
                      <tr>
                        <td colSpan={6}>
                          <ListPageTablePlaceholder>No items found.</ListPageTablePlaceholder>
                        </td>
                      </tr>
                    ) : (
                      items.map((item) => (
                        <tr key={item.costItemId} className={listPageTableRowClass}>
                          <td className="px-5 py-4 text-white font-medium">{item.itemName}</td>
                          <td className="px-5 py-4">
                            <span
                              className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${getCategoryBadgeColor(item.categoryName)}`}
                            >
                              {item.categoryName}
                            </span>
                          </td>
                          <td className="px-5 py-4 text-slate-300">{item.unitName}</td>
                          <td className="px-5 py-4 text-white font-semibold">{formatCurrency(item.lowestPrice)}</td>
                          <td className="px-5 py-4 text-slate-300">{formatOutput(item)}</td>
                          <td className="px-5 py-4 text-right">
                            <div className="action-row-icon-buttons">
                              {canEditCostItem && (
                                <button
                                  type="button"
                                  onClick={() => handleOpenEditDialog(item.costItemId)}
                                  className="text-primary hover:bg-primary/10"
                                  aria-label="Edit item"
                                >
                                  <span className="material-symbols-outlined text-base">edit</span>
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
              </table>
            </div>
          </ListPageTableSurface>

          <ListPageMobileStack>
            {loading ? (
              <ListPageMobilePlaceholder>Loading items...</ListPageMobilePlaceholder>
            ) : items.length === 0 ? (
              <ListPageMobilePlaceholder>
                {error ? `Error: ${error}` : 'No items found.'}
              </ListPageMobilePlaceholder>
            ) : (
              items.map((item) => (
                <div
                  key={item.costItemId}
                  className="bg-elevation-dark rounded-xl p-4 border border-border-dark shadow-lg flex flex-col gap-3"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="material-symbols-outlined text-base text-primary shrink-0">inventory_2</span>
                        <h3 className="text-white font-semibold text-lg truncate">{item.itemName}</h3>
                      </div>
                      <span
                        className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-semibold ${getCategoryBadgeColor(item.categoryName)}`}
                      >
                        {item.categoryName}
                      </span>
                    </div>
                  </div>

                  <div className="space-y-2 text-sm">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-slate-500 flex items-center gap-1">
                        <span className="material-symbols-outlined text-sm">straighten</span>
                        Unit
                      </span>
                      <span className="text-white">{item.unitName}</span>
                    </div>
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-slate-500 flex items-center gap-1">
                        <span className="material-symbols-outlined text-sm">attach_money</span>
                        Lowest Price
                      </span>
                      <span className="text-white font-semibold">{formatCurrency(item.lowestPrice)}</span>
                    </div>
                    {item.outputRate && item.outputRateUnitSymbol && (
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-slate-500 flex items-center gap-1">
                          <span className="material-symbols-outlined text-sm">speed</span>
                          Output
                        </span>
                        <span className="text-white">{formatOutput(item)}</span>
                      </div>
                    )}
                  </div>

                  <div className="action-row-icon-buttons border-t border-border-dark/50 pt-3">
                    {canEditCostItem && (
                      <button
                        type="button"
                        onClick={() => handleOpenEditDialog(item.costItemId)}
                        className="text-primary hover:bg-primary/10"
                        aria-label="Edit"
                      >
                        <span className="material-symbols-outlined">edit</span>
                      </button>
                    )}
                  </div>
                </div>
              ))
            )}
          </ListPageMobileStack>
        </ListPageContentSection>

        {totalPages > 1 && (
          <div className="px-4 lg:px-5 pb-4">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={(page) => {
                setCurrentPage(page);
              }}
            />
          </div>
        )}

        <CostItemDialog
          open={dialogOpen}
          onClose={handleCloseDialog}
          costItemId={selectedCostItemId}
          onSuccess={handleDialogSuccess}
          onError={handleDialogError}
        />

        {dialogError && (
          <div className="fixed top-4 left-1/2 transform -translate-x-1/2 z-50 bg-red-500/10 border border-red-500/20 rounded-lg p-4 text-red-400 text-sm max-w-md">
            <div className="flex items-center justify-between">
              <span>{dialogError}</span>
              <button
                type="button"
                onClick={() => setDialogError('')}
                className="ml-4 text-red-400 hover:text-red-300"
              >
                <span className="material-symbols-outlined text-base">close</span>
              </button>
            </div>
          </div>
        )}
      </ListPageRoot>
    </DarkLayout>
  );
};

export default CostLibrary;
